import { createSlice } from '@reduxjs/toolkit';
import {
    setSocketConnectivity,
    setSocketInstance,
    addParticipant,
    removeParticipant,
    newMessageReceived,
    engagementCreated,
    setTwilioClient,
    setTwilioConversation,
    setTwilioMessages,
    addTwilioMessage,
    setTwilioParticipants,
    updateTwilioParticipant,
    removeTwilioParticipant,
    setTwilioUsers,
    updateTwilioMessage,
    removeTwilioMessage,
} from '../actions/chatActions';

interface ChatState {
    isSocketConnected: boolean;
    socketInstance: any;
    messages: any[];
    participants: any[];
    isBotConnected: boolean;
    engagementParam: any;
    // Twilio migration bridge
    conversation?: any;
    conversationsClient?: any;
    users?: any[];
}

const initialState: ChatState = {
    isSocketConnected: false,
    socketInstance: null,
    messages: [],
    participants: [],
    isBotConnected: false,
    engagementParam: null,
    conversation: undefined,
    conversationsClient: undefined,
};

const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(setSocketConnectivity, (state, action) => {
                state.isSocketConnected = action.payload;
            })
            .addCase(setSocketInstance, (state, action) => {
                state.socketInstance = action.payload;
            })
            .addCase(setTwilioClient, (state, action) => {
                state.conversationsClient = action.payload;
            })
            .addCase(setTwilioConversation, (state, action) => {
                state.conversation = action.payload;
            })
            .addCase(setTwilioMessages, (state, action) => {
                state.messages = action.payload || [];
            })
            .addCase(addTwilioMessage, (state, action) => {
                state.messages = [...(state.messages || []), action.payload];
            })
            .addCase(updateTwilioMessage, (state, action) => {
                state.messages = (state.messages || []).map((m: any) => (m.sid === action.payload.sid ? action.payload : m));
            })
            .addCase(removeTwilioMessage, (state, action) => {
                state.messages = (state.messages || []).filter((m: any) => m.sid !== action.payload.sid);
            })
            .addCase(setTwilioParticipants, (state, action) => {
                state.participants = action.payload || [];
            })
            .addCase(updateTwilioParticipant, (state, action) => {
                state.participants = (state.participants || []).map((p: any) => (p.sid === action.payload.sid ? action.payload : p));
            })
            .addCase(removeTwilioParticipant, (state, action) => {
                state.participants = (state.participants || []).filter((p: any) => p.sid !== action.payload.sid);
            })
            .addCase(setTwilioUsers, (state, action) => {
                state.users = action.payload || [] as any[];
            })
            .addCase(newMessageReceived, (state, action) => {
                state.messages.push(action.payload);
                state.isBotConnected = action.payload?.senderParticipantType === "SYSTEM";
            })
            .addCase(addParticipant, (state, action) => {
                const participant = action.payload;

                // Ensure participant is defined before accessing its properties
                if (!participant) {
                    console.error("Participant data is undefined", action.payload);
                    return;
                }

                const currentTime = Date.now();
                state.participants.push(participant);

                if (participant.participantType === "AGENT") {
                    state.messages.push({
                        body: { elementText: { text: participant.displayName }, elementType: "text" },
                        senderType: "PARTICIPANT_ADDED",
                        lastUpdatedTimestamp: currentTime,
                    });
                }

                state.isBotConnected = false;
            })
            .addCase(removeParticipant, (state, action) => {
                const participant = action.payload;
                const currentTime = Date.now();

                // Ensure participant is defined before accessing its properties
                if (!participant) {
                    console.error("Participant data is undefined", action.payload);
                    return;
                }

                state.participants = state.participants.filter(
                    (p) => p.participantId !== participant.participantId
                );

                if (participant.participantType === "AGENT") {
                state.messages.push({
                    body: { elementText: { text: participant.displayName }, elementType: "text" },
                    senderType: "PARTICIPANT_REMOVED",
                    lastUpdatedTimestamp: currentTime,
                });
                }

                state.isBotConnected = false;
            })
            .addCase(engagementCreated, (state, action) => {
                if (action.payload?.eventType === "ENGAGEMENT_CREATED") {
                    state.engagementParam = { ...action.payload?.engagementParameters, engagement_id: action.payload?.engagementId };
                }
            });
    }
});

export default chatSlice.reducer;
